import { Form, Formik, FormikErrors } from 'formik';
import { FormFormattedTextField, Grid } from 'ui';
import { InputAdornment } from '@material-ui/core';
import { StoreType } from '@tock/shared/utils/connect';
import { SubmitButton, Typography } from 'omakase';
import { Theme, css } from '@emotion/react';
import { pluralize } from '@tock/shared/utils/pluralize';
import { requireFields } from '@tock/shared/utils/FormValidators';
import _ from 'lodash';
import numberAllowUndefinedFilter from '@tock/shared/filters/numberAllowUndefined';

export interface Fields {
  points: string;
}

interface Props {
  loyaltyProgram: StoreType['loyaltyProgram'];
  onSubmit(values: Fields): void;
  showDollarSign?: boolean;
}

const formWrapper = ({ spacing }: Theme) => css`
  margin: ${spacing(3)}px;
`;

const LoyaltyProgramRedemptionForm: React.FunctionComponent<Props> = ({
  loyaltyProgram,
  onSubmit,
  showDollarSign,
}) => {
  const numPointsToRedeem = loyaltyProgram.balance?.numPointsToRedeem || 0;

  const validate = (values: Fields) => {
    const errors: FormikErrors<Fields> = requireFields(values, 'points');
    if (_.toNumber(values.points) > numPointsToRedeem) {
      errors.points = `Please enter a value less than or equal to ${numPointsToRedeem}.`;
    }
    if (_.toNumber(values.points) < 0) {
      errors.points = 'Please enter a value greater than 0.';
    }
    return errors;
  };

  return (
    <Formik<Fields>
      initialValues={{ points: _.toString(loyaltyProgram?.numPointsToRedeem || 0) }}
      onSubmit={onSubmit}
      validate={validate}
    >
      {({ isSubmitting, isValid }) => (
        <Form css={formWrapper}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body02">
                You have {showDollarSign ? '$' : ''}
                {pluralize(numPointsToRedeem, 'Reward Dollar')} available for this reservation. How
                many would you like to use?
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormFormattedTextField
                name="points"
                filter={numberAllowUndefinedFilter}
                label="Reward dollars to redeem"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <SubmitButton
                variant="primary"
                fullWidth
                disabled={!isValid || isSubmitting}
                isSubmitting={isSubmitting}
                size="large"
              >
                Apply and finish
              </SubmitButton>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default LoyaltyProgramRedemptionForm;
